import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { MapPin, CalendarDays, Users, Search } from "lucide-react";

const toISO = (d) => (d ? format(d, "yyyy-MM-dd") : "");

export default function SearchBar({ initial = {}, compact = false }) {
  const navigate = useNavigate();
  const [location, setLocation] = useState(initial.location || "");
  const [range, setRange] = useState({
    from: initial.check_in ? parseISO(initial.check_in) : undefined,
    to: initial.check_out ? parseISO(initial.check_out) : undefined,
  });
  const [guests, setGuests] = useState(Number(initial.guests) || 1);
  const pastDays = useMemo(() => ({ before: new Date() }), []);

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (location) p.set("location", location);
    if (range?.from && range?.to) {
      p.set("check_in", toISO(range.from));
      p.set("check_out", toISO(range.to));
    }
    if (guests > 1) p.set("guests", guests);
    navigate(`/search?${p.toString()}`);
  };

  const dateLabel = range?.from ? `${format(range.from, "MMM d")} – ${range.to ? format(range.to, "MMM d") : "?"}` : "Add dates";
  const cell = "flex min-w-0 flex-1 items-center gap-3 px-5 py-3";

  return (
    <form onSubmit={submit} data-testid="search-bar" className={`flex w-full flex-col divide-y rounded-3xl border bg-white shadow-xl shadow-stone-900/5 md:flex-row md:items-center md:divide-x md:divide-y-0 md:rounded-full ${compact ? "md:shadow-sm" : ""}`}>
      <label className={cell}>
        <MapPin className="h-5 w-5 shrink-0 text-primary" />
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500">Where</span>
          <input data-testid="search-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City or region" className="w-full bg-transparent text-sm outline-none placeholder:text-stone-400" />
        </span>
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={`${cell} text-left`} data-testid="search-dates-trigger">
            <CalendarDays className="h-5 w-5 shrink-0 text-primary" />
            <span>
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500">When</span>
              <span className="text-sm">{dateLabel}</span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="range" numberOfMonths={2} selected={range} onSelect={setRange} disabled={pastDays} data-testid="search-calendar" />
        </PopoverContent>
      </Popover>
      <div className={cell}>
        <Users className="h-5 w-5 shrink-0 text-primary" />
        <span className="flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500">Guests</span>
          <span className="flex items-center gap-3 text-sm">
            <button type="button" data-testid="search-guests-minus" className="h-9 w-9 rounded-full border hover:bg-stone-100" onClick={() => setGuests(Math.max(1, guests - 1))}>−</button>
            <span data-testid="search-guests-value" className="font-mono-num">{guests}</span>
            <button type="button" data-testid="search-guests-plus" className="h-9 w-9 rounded-full border hover:bg-stone-100" onClick={() => setGuests(Math.min(20, guests + 1))}>+</button>
          </span>
        </span>
      </div>
      <div className="p-2">
        <Button type="submit" data-testid="search-submit-button" className="h-12 w-full rounded-full px-6 md:w-auto">
          <Search className="mr-2 h-4 w-4" /> Search
        </Button>
      </div>
    </form>
  );
}
